import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ImageOff } from "lucide-react";
import { FadeUp, SectionHeading, EASE } from "@/components/Reveal";
import { API, api, resolveImg, formatApiErrorDetail } from "@/lib/api";

export const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/gallery")
      .then(({ data }) => mounted && setPhotos(data))
      .catch((e) => mounted && setError(formatApiErrorDetail(e)))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(photos.map((p) => p.category).filter(Boolean)))],
    [photos]
  );
  const shown = active === "All" ? photos : photos.filter((p) => p.category === active);

  return (
    <section id="gallery" data-testid="photo-gallery-section" className="relative py-24 lg:py-32 bg-[#120204]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Gallery"
          title={
            <>
              A Glimpse of <span className="italic gold-text">the Grandeur</span>
            </>
          }
          sub="A living album of mandap stages, reception decor and celebration moments — refreshed by us as the venue comes to life."
        />

        <FadeUp delay={0.1} className="mt-10 flex flex-wrap gap-2.5">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setActive(c)}
              className={`rounded-full px-5 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 border ${
                active === c
                  ? "bg-[#D4AF37] text-[#2C070B] border-[#D4AF37]"
                  : "border-[#D4AF37]/30 text-[#A39791] hover:text-[#F3E5AB] hover:border-[#D4AF37]/70"
              }`}
            >
              {c}
            </button>
          ))}
        </FadeUp>

        <div className="mt-10">
          {loading && (
            <div data-testid="gallery-loading" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-[#D4AF37]/15 bg-[#2C070B]/40 animate-pulse"
                  style={{ height: `${220 + (i % 3) * 70}px` }}
                />
              ))}
            </div>
          )}

          {!loading && error && (
            <p data-testid="gallery-error" className="text-sm text-[#A39791] flex items-center gap-2">
              <ImageOff size={16} /> Photos could not be loaded right now. Please refresh in a moment.
            </p>
          )}

          {!loading && !error && shown.length === 0 && (
            <p data-testid="gallery-empty" className="text-sm text-[#A39791]">
              Photos are arriving soon — our first album is being prepared.
            </p>
          )}

          {!loading && !error && shown.length > 0 && (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
              {shown.map((p, i) => (
                <motion.button
                  key={p.id}
                  data-testid={`gallery-item-${p.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.08 }}
                  onClick={() => setLightbox(p)}
                  className="group relative block w-full mb-5 break-inside-avoid overflow-hidden rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/60 transition-[border-color] duration-500 text-left"
                >
                  <img
                    src={resolveImg(p)}
                    alt={p.title || "Mandap gallery photo"}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#120204]/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 inset-x-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <p className="font-serif-lux text-xl text-[#F3E5AB]">{p.title}</p>
                    <p className="text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mt-1">{p.category}</p>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[95] bg-[#120204]/95 backdrop-blur-md flex items-center justify-center p-5 cursor-zoom-out"
          >
            <motion.div
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 12 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={resolveImg(lightbox)}
                alt={lightbox.title || "Mandap gallery photo"}
                className="w-full max-h-[80vh] object-contain rounded-2xl border border-[#D4AF37]/35"
              />
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="font-serif-lux text-2xl text-[#F3E5AB]">{lightbox.title}</p>
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mt-1">{lightbox.category}</p>
                </div>
                <button
                  data-testid="gallery-lightbox-close"
                  onClick={() => setLightbox(null)}
                  className="rounded-full border border-[#D4AF37]/40 p-3 text-[#F3E5AB] hover:bg-[#D4AF37] hover:text-[#2C070B] transition-colors duration-300"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
